import clsx from "clsx";

import { motion } from "../../components/presence";
import { t } from "../../i18n/i18n";
import { formatKm, formatKmBound } from "./corridorWidths";
import type { GestureTag } from "./gestureState";

/** Puce du geste en cours, posée sur la carte (docs/14 §2.3, docs/15 §4.6) : largeur de la paire pendant le
 *  glissement d'un sommet seul (« 19,40 km · doctrinale 8,00 km (6 à 10) » ; « largeur doctrinale », ton
 *  « calé », une fois aimantée sur la médiane du mémento ; ton d'alerte selon la règle d'alerte de largeur), ou
 *  règle violée par la géométrie (« Les limites se croisent »). Même gabarit que les étiquettes des LIMA (puce
 *  de l'interface), transparente aux gestes. */

/** Texte de la puce de largeur. */
export function widthText(tag: Extract<GestureTag, { kind: "width" }>): string {
  if (tag.snapped) return t("{width} · largeur doctrinale", { width: formatKm(tag.doctrinal) });
  if (!tag.range || !Number.isFinite(tag.doctrinal)) return formatKm(tag.width);
  return t("{width} · doctrinale {doctrinal} ({min} à {max})", {
    width: formatKm(tag.width),
    doctrinal: formatKm(tag.doctrinal),
    min: formatKmBound(tag.range[0]),
    max: formatKmBound(tag.range[1]),
  });
}

/** Ton de la puce : aimantée, alerte de largeur (paire retouchée que le geste éloigne de la plage doctrinale),
 *  refus pour une règle violée. */
export function widthTone(tag: GestureTag): "neutral" | "ok" | "warn" | "danger" {
  if (tag.kind === "rule") return "danger";
  if (tag.snapped) return "ok";
  return tag.alert ? "warn" : "neutral";
}

export function WidthTag({ tag, closing = false }: { tag: GestureTag; closing?: boolean }) {
  const tone = widthTone(tag);
  return (
    // Visuelle seulement : elle change à chaque image du geste, une zone `aria-live` noierait le lecteur d'écran
    // (l'annonce accessible passe par le bandeau).
    <div
      aria-hidden
      className={clsx(
        motion("zoom", closing),
        "pointer-events-none flex h-6 items-center gap-1.5 rounded-md border px-2 font-sans whitespace-nowrap shadow-[0_1px_2px_rgb(16_18_24/0.14)]",
        tone === "danger"
          ? "border-brand/30 bg-brand-soft text-brand"
          : tone === "warn"
            ? "border-warn/30 bg-warn-soft text-warn"
            : tone === "ok"
              ? "border-ok/30 bg-ok-soft text-ok"
              : "border-line-strong bg-surface text-ink",
      )}
    >
      {tag.kind === "width" ? (
        <span className="text-[11.5px] leading-none font-medium tabular-nums">
          {widthText(tag)}
        </span>
      ) : (
        <span className="text-[11.5px] leading-none font-medium">{tag.rule}</span>
      )}
    </div>
  );
}
